<script lang="ts" setup>
  import { ref, computed, onMounted, watch } from 'vue'
  import AgentActionBaseWidget from './AgentActionBaseWidget.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import SkeletonLoaderInlineField from '@/components/skeleton/SkeletonLoaderInlineField.vue'
  import type { Option } from '@/components/admin/form/SelectField.vue'
  import { IconScale } from '@tabler/icons-vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { useLawFieldStore } from '@/store/lawFieldStore'
  import { useUserStore } from '@/shared/stores/userStore'
  import { LawFieldsService } from '@/types/generated'

  // Translation setup
  const { t } = useTranslation({
    en: {
      selectLawField: 'Select law field...',
      selectLawTopic: 'Select law topic...',
      noLawFieldsFound: 'No law fields found',
      noLawTopicsFound: 'No law topics found',
      assignText: 'Assign law field',
      andTopicText: 'and topic',
      toTheCase: 'to the case.',
      lawDomainAssigned: 'The law field',
      andTopic: 'and topic',
      wereAssignedToCase: 'were assigned to the case.'
    },
    de: {
      selectLawField: 'Rechtsgebiet auswählen...',
      selectLawTopic: 'Rechtsthema auswählen...',
      noLawFieldsFound: 'Keine Rechtsgebiete gefunden',
      noLawTopicsFound: 'Keine Rechtsthemen gefunden',
      assignText: 'Setze das Rechtsgebiet',
      andTopicText: 'und das Thema',
      toTheCase: 'für die Anfrage.',
      lawDomainAssigned: 'Das Rechtsgebiet',
      andTopic: 'und das Thema',
      wereAssignedToCase: 'wurden der Anfrage zugewiesen.'
    }
  })

  // Define props
  interface Props {
    action: any // TAgentAction type from generated types
    customPayload?: any // Optional custom payload from parent
  }

  const props = defineProps<Props>()

  // Define emits
  const emit = defineEmits<{
    payloadChanged: [payload: any]
  }>()

  // Stores
  const {
    fetchLawFields,
    getLawFieldOptions,
    getLawTopicOptions,
    lawFields: storeLawFields
  } = useLawFieldStore()
  const { user, fetchUser } = useUserStore()

  // Reactive state
  const isLoadingLawFields = ref(false)
  const selectedLawField = ref<string>('')
  const selectedLawTopic = ref<string>('')
  const localLawFields = ref<any>(null)

  // Computed properties - get ALL available law fields
  const lawFieldOptions = computed<Option[]>(() => {
    const lawFieldsData = localLawFields.value || storeLawFields.value

    if (!lawFieldsData?.data) {
      return []
    }

    // Get ALL available law fields from the law fields service
    const options = Object.keys(lawFieldsData.data)
      .map((lawField) => ({
        value: lawField,
        title: lawField
      }))
      .sort((a, b) => a.title.localeCompare(b.title))

    return options
  })

  const lawTopicOptions = computed<Option[]>(() => {
    if (!selectedLawField.value) return []

    const lawFieldsData = localLawFields.value || storeLawFields.value
    if (!lawFieldsData?.data || !lawFieldsData.data[selectedLawField.value]) {
      return []
    }

    const options = (lawFieldsData.data[selectedLawField.value] || [])
      .map((topic: string) => ({
        value: topic,
        title: topic
      }))
      .sort((a: any, b: any) => a.value.localeCompare(b.value))

    return options
  })

  // Check if action should show summary (committed, reverted, or has error)
  const shouldShowSummary = computed(
    () =>
      props.action?.is_committed === true ||
      props.action?.is_reverted === true ||
      props.action?.has_commit_error === true ||
      (props.action?.is_commitable === false && selectedLawField.value && selectedLawTopic.value)
  )

  // Summary text for committed/cancelled/error actions
  const summaryText = computed(() => {
    if (!shouldShowSummary.value || !selectedLawField.value || !selectedLawTopic.value) return ''

    return `${t('lawDomainAssigned')} "${selectedLawField.value}" ${t('andTopic')} "${selectedLawTopic.value}" ${t('wereAssignedToCase')}`
  })

  // Methods
  const handleLawFieldSelection = (lawField: string) => {
    selectedLawField.value = lawField
    // Reset topic when field changes
    selectedLawTopic.value = ''
    emitPayloadChange()
  }

  const handleLawTopicSelection = (lawTopic: string) => {
    selectedLawTopic.value = lawTopic
    emitPayloadChange()
  }

  // Emit payload changes to parent
  const emitPayloadChange = () => {
    if (props.action?.payload?.case_uuid) {
      const payload = {
        case_uuid: props.action.payload.case_uuid,
        law_field: selectedLawField.value,
        law_topic: selectedLawTopic.value
      }
      emit('payloadChanged', payload)
    }
  }

  // Initialize selections from action payload
  const initializeFromPayload = (payload: any) => {
    if (!payload) return

    if (payload.law_field) {
      selectedLawField.value = payload.law_field
    }
    if (payload.law_topic) {
      selectedLawTopic.value = payload.law_topic
    }
  }

  // Watch for action payload changes
  watch(
    () => props.action?.payload,
    (newPayload) => {
      initializeFromPayload(newPayload)
    },
    { deep: true }
  )

  // Watch for custom payload changes
  watch(
    () => props.customPayload,
    (newCustomPayload) => {
      if (newCustomPayload) {
        initializeFromPayload(newCustomPayload)
      }
    },
    { deep: true }
  )

  // Initialize component
  onMounted(async () => {
    // Set initial selection from action payload or custom payload
    initializeFromPayload(props.customPayload || props.action?.payload)

    // Fetch data if needed
    isLoadingLawFields.value = true
    try {
      const promises = []

      // Only fetch user if not already loaded
      if (!user.value) {
        promises.push(fetchUser())
      }

      // Always fetch law fields for topics - use direct service call
      const lawFieldsPromise = LawFieldsService.lawFieldsRetrieve().then((result) => {
        localLawFields.value = result
        // Also try to update the store
        try {
          storeLawFields.value = result
        } catch (e) {
          // Store update failed, but local data is available
        }
      })
      promises.push(lawFieldsPromise)

      await Promise.all(promises)
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      isLoadingLawFields.value = false
    }

    // Emit initial payload if any values are set
    if (selectedLawField.value && selectedLawTopic.value) {
      emitPayloadChange()
    }
  })
</script>

<template>
  <div>
    <!-- Action Summary (committed, cancelled, reverted, or error) -->
    <div
      v-if="shouldShowSummary"
      class="space-y-3"
    >
      <div class="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span>{{ summaryText }}</span>
      </div>
    </div>

    <!-- Action Details (when editable) -->
    <div
      v-else
      class="space-y-3"
    >
      <div class="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span>{{ t('assignText') }}</span>

        <!-- Law Field Selection -->
        <template v-if="isLoadingLawFields">
          <SkeletonLoaderInlineField
            width="160px"
            height="32px"
            class="inline-block"
          />
        </template>
        <template v-else>
          <SelectField
            v-model="selectedLawField"
            variant="inline-text"
            :options="lawFieldOptions"
            :is-filterable="false"
            :disabled="isLoadingLawFields"
            :placeholder="
              lawFieldOptions.length === 0 ? t('noLawFieldsFound') : t('selectLawField')
            "
            name="lawField"
            @update:model-value="handleLawFieldSelection"
          />
        </template>

        <span>{{ t('andTopicText') }}</span>

        <!-- Law Topic Selection -->
        <template v-if="isLoadingLawFields">
          <SkeletonLoaderInlineField
            width="160px"
            height="32px"
            class="inline-block"
          />
        </template>
        <template v-else>
          <SelectField
            v-model="selectedLawTopic"
            variant="inline-text"
            :options="lawTopicOptions"
            :is-filterable="false"
            :disabled="isLoadingLawFields || !selectedLawField"
            :placeholder="
              lawTopicOptions.length === 0 ? t('noLawTopicsFound') : t('selectLawTopic')
            "
            name="lawTopic"
            @update:model-value="handleLawTopicSelection"
          />
        </template>

        <span>{{ t('toTheCase') }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
